import { SaleChannel } from '@/constants'
import { useAuth } from '@/containers'
import type { Property, PropertyValue } from '@/types'
import { getTranslate } from '@/utils/api-interceptors'
import { useState, type ReactNode } from 'react'
import DrawCenter from '../DrawCenter/DrawCenter'
import ImageOptimize from '../ImageOptimize'

interface ProductSpecificationsProps {
  specifications: Property[]
  className?: string
  onViewAll: () => void
}

export const ProductSpecifications = ({
  specifications,
  className,
  onViewAll,
}: ProductSpecificationsProps) => {
  const { token, user } = useAuth()
  const [content, setContent] = useState<string | null>(null)

  const specificationsData = specifications?.filter(
    item => !item?.policy && item?.channels?.includes(SaleChannel.B2C)
  )

  const specificationsDataB2E = specifications?.filter(
    item => !item?.policy && item?.channels?.includes(SaleChannel.B2E)
  )

  const policyData = specifications?.filter(
    item => item?.policy && item?.channels?.includes(SaleChannel.B2C)
  )

  const policyDataB2E = specifications?.filter(
    item => item?.policy && item?.channels?.includes(SaleChannel.B2E)
  )

  const renderSpecificationsHighlight = (item: Property): ReactNode => {
    switch (item.type as string) {
      case 'multipleSelect':
        return item.values.map((e: PropertyValue) => {
          return (
            <span
              key={e._id}
              className="mr-1 inline-block after:content-[','] last:mr-0 last:after:content-[unset]"
            >
              {getTranslate(e.value)}
            </span>
          )
        })

      case 'singleSelect':
        return item.values.map((e: PropertyValue) => {
          return e.link ? (
            <span
              className="mr-1 inline-block cursor-pointer text-blue after:content-[','] last:mr-0 last:after:content-[unset]"
              key={e._id}
            >
              {getTranslate(e.value)}
            </span>
          ) : (
            <span
              className="mr-1 inline-block after:content-[','] last:mr-0 last:after:content-[unset]"
              key={e._id}
            >
              {getTranslate(e.value)}
            </span>
          )
        })

      case 'input':
        return <span>{getTranslate(item.value)}</span>
      default:
        break
    }
  }

  const renderSpecifications = (properties: Property[]) => (
    <section className='rounded-xl border border-[#D1D5DB] bg-white p-3 pt-5'>
      <div className='flex items-center justify-between '>
        <h3 className='text-lg font-semibold text-main'>Thông số kỹ thuật</h3>
        <button
          className='whitespace-nowrap border-none text-sm text-link outline-none'
          onClick={onViewAll}
        >
          Xem tất cả
        </button>
      </div>

      <div className='mt-4 flex flex-wrap gap-x-5 gap-y-2'>
        {properties.map(item => (
          <div
            className='flex gap-3 rounded-sm bg-[#F3F4F6] p-3'
            style={{ width: 'calc(50% - 10px)' }}
            key={item._id}
          >
            <ImageOptimize
              src={item.image || ''}
              alt={getTranslate(item.name)}
              className='mt-1 h-[24px] w-[24px]'
            />
            <div className='grow'>
              <div className='text-sm font-semibold text-main'>
                {getTranslate(item.name)}
              </div>
              <div className='mt-0.5 line-clamp-2 text-sm text-main'>
                {renderSpecificationsHighlight(item)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  )

  const renderPolicy = (properties: Property[]) => (
    <section>
      <h3 className='text-base font-semibold text-main'>Chính sách bán hàng</h3>

      <div className='mt-4 flex flex-wrap gap-x-5 gap-y-3'>
        {properties.map(item => (
          <div
            className='flex items-start gap-2 rounded-sm'
            style={{ width: 'calc(50% - 10px)' }}
            key={item._id}
          >
            <ImageOptimize
              src={item.image || ''}
              alt={getTranslate(item.title)}
              className='h-6 w-6'
              width={24}
              height={24}
            />
            <span
              className='line-clamp-2 cursor-pointer text-sm font-semibold text-primary hover:underline'
              onClick={() => setContent(getTranslate(item.content))}
            >
              {getTranslate(item.name)}
            </span>
          </div>
        ))}
      </div>
    </section>
  )

  const renderSpecificationsData =
    token && user?.saleChannel === SaleChannel.B2E
      ? specificationsDataB2E
      : specificationsData

  const renderPolicyData =
    user?.saleChannel == SaleChannel.B2E ? policyDataB2E : policyData

  return (
    <div className={`${className} flex flex-col gap-5`}>
      {!!renderSpecificationsData?.length &&
        renderSpecifications(renderSpecificationsData.slice(0, 4))}
      {!!renderPolicyData?.length && renderPolicy(renderPolicyData)}

      {!!content && (
        <DrawCenter
          open
          onClose={() => setContent(null)}
          title='Chính sách bán hàng'
        >
          <div dangerouslySetInnerHTML={{ __html: content || '' }} />
        </DrawCenter>
      )}
    </div>
  )
}

export default ProductSpecifications
